import { useState } from 'react';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useQuery } from '@tanstack/react-query';
import { ProductServiceSchema } from '@taqon/contracts';
import { Link as RouterLink, useParams } from 'react-router';
import { getData } from '../../shared/api/client';
import { RecordHistory } from '../../shared/audit/AuditTrail';
import { hasCapability, useSession } from '../../shared/session/session';
import { StatusChip } from '../../shared/ui/forms';
import { LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { Facts, RecordFailure } from '../finance/detail';
import {
  PRODUCTS_SERVICES_PATH,
  ProductServiceDialog,
  ProductServiceStatusDialog,
} from './ProductsServicesPage';
import { WorkersSection } from '../hr/EmployeeWork';
import { MigrationTrace } from '../migration/MigrationTrace';

/**
 * One product or service at its own address (ERD §4.3; FRD-CORE-002): its facts, Edit and a
 * confirmed Activate / Deactivate where the session holds the change permission, and its audit
 * history. No transaction refers to it in this release, so it has no related records to list.
 */
export function ProductServiceDetailPage() {
  const { id = '' } = useParams();
  const session = useSession();
  const canChange = hasCapability(session.data, 'organization.product_service:change');
  const [dialog, setDialog] = useState<'edit' | 'status' | null>(null);
  const item = useQuery({
    queryKey: ['products-services', 'one', id],
    queryFn: () => getData(`${PRODUCTS_SERVICES_PATH}/${id}`, ProductServiceSchema),
  });
  const back = (
    <Button
      component={RouterLink}
      to="/organization/products-services"
      startIcon={<ArrowBackIcon />}
    >
      All products & services
    </Button>
  );
  if (item.isPending) return <LoadingState label="Loading the product or service" />;
  if (item.isError) {
    return (
      <RecordFailure
        title="Product or service"
        noun="product or service"
        error={item.error}
        back={back}
      />
    );
  }
  const row = item.data;
  return (
    <Stack spacing={3}>
      <PageHeader
        title={`${row.code} — ${row.name}`}
        description="Product or service"
        actions={
          <>
            {back}
            {canChange && (
              <>
                <Button
                  variant="outlined"
                  color={row.status === 'active' ? 'warning' : 'primary'}
                  onClick={(event) => {
                    event.currentTarget.focus();
                    setDialog('status');
                  }}
                >
                  {row.status === 'active' ? 'Deactivate' : 'Activate'}
                </Button>
                <Button
                  variant="contained"
                  onClick={(event) => {
                    event.currentTarget.focus();
                    setDialog('edit');
                  }}
                >
                  Edit
                </Button>
              </>
            )}
          </>
        }
      />
      <Section title="Product or service">
        <Facts
          label="Product or service details"
          facts={[
            ['Code', row.code],
            ['Name', row.name],
            ['Description', row.description ?? '—'],
            ['Status', <StatusChip key="s" status={row.status} />],
          ]}
        />
      </Section>
      <WorkersSection filter={{ productServiceId: row.id }} />
      <MigrationTrace recordType="organization.product_service" recordId={row.id} />
      <RecordHistory recordType="organization.product_service" recordId={row.id} />
      {dialog === 'edit' && (
        <ProductServiceDialog
          key={String(row.version)}
          item={row}
          onClose={() => {
            setDialog(null);
          }}
        />
      )}
      {dialog === 'status' && (
        <ProductServiceStatusDialog
          item={row}
          onClose={() => {
            setDialog(null);
          }}
        />
      )}
    </Stack>
  );
}
